import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { useAuth } from '../AuthContext';
import { localDateFromYMD } from '../utils/dbTime';

// Work order statuses
const WORK_ORDER_STATUSES = [
  { id: 'open', label: 'Open', color: '#58a6ff' },
  { id: 'in_progress', label: 'In Progress', color: '#d29922' },
  { id: 'waiting_parts', label: 'Waiting Parts', color: '#a371f7' },
  { id: 'completed', label: 'Completed', color: '#238636' }
];

// Priority levels
const PRIORITY_LEVELS = [
  { id: 'low', label: 'Low', color: '#8b949e' },
  { id: 'medium', label: 'Medium', color: '#d29922' },
  { id: 'high', label: 'High', color: '#da3633' }
];

function MaintenanceView() {
  const { isOwner } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [trucks, setTrucks] = useState([]);
  const [selectedTruck, setSelectedTruck] = useState(null);
  const [maintenanceRecords, setMaintenanceRecords] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [driverRequests, setDriverRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddWorkOrder, setShowAddWorkOrder] = useState(false);
  const [showAddRecord, setShowAddRecord] = useState(false);

  const [newWorkOrder, setNewWorkOrder] = useState({
    truck_id: '',
    title: '',
    description: '',
    priority: 'medium',
    status: 'open',
    assigned_to: '',
    estimated_cost: '',
    due_date: ''
  });

  const [newRecord, setNewRecord] = useState({
    maintenance_type: 'oil_change',
    description: '',
    performed_at_km: '',
    performed_date: new Date().toISOString().split('T')[0],
    cost: '',
    next_due_km: ''
  });

  const maintenanceTypes = [
    { value: 'oil_change', label: 'Oil Change', interval: 8000 },
    { value: 'tire_rotation', label: 'Tire Rotation', interval: 12000 },
    { value: 'brake_inspection', label: 'Brake Inspection', interval: 20000 },
    { value: 'transmission_service', label: 'Transmission Service', interval: 50000 },
    { value: 'air_filter', label: 'Air Filter', interval: 25000 },
    { value: 'fuel_filter', label: 'Fuel Filter', interval: 40000 },
    { value: 'coolant_flush', label: 'Coolant Flush', interval: 50000 },
    { value: 'inspection', label: 'Safety Inspection', interval: 0 },
    { value: 'custom', label: 'Custom/Other', interval: 0 }
  ];

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedTruck) {
      fetchMaintenanceRecords(selectedTruck.id);
    }
  }, [selectedTruck]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch trucks
      const { data: trucksData } = await supabase
        .from('trucks')
        .select('*')
        .eq('is_active', true)
        .order('truck_number');
      setTrucks(trucksData || []);
      if (trucksData && trucksData.length > 0) {
        setSelectedTruck(trucksData[0]);
      }

      // Fetch work orders (from local state for now - would be from DB in production)
      // For demo, we'll use local state
      
      // Fetch driver maintenance requests (simplified query to avoid join errors)
      try {
        const { data: requestsData, error: requestsError } = await supabase
          .from('maintenance_requests')
          .select('*, truck:trucks(truck_number, license_plate)')
          .order('created_at', { ascending: false });
        if (!requestsError) {
          setDriverRequests(requestsData || []);
        } else {
          console.log('maintenance_requests table may not exist:', requestsError.message);
          setDriverRequests([]);
        }
      } catch (e) {
        console.log('Error fetching maintenance requests:', e);
        setDriverRequests([]);
      }

    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMaintenanceRecords = async (truckId) => {
    try {
      const { data } = await supabase
        .from('maintenance_records')
        .select('*')
        .eq('truck_id', truckId)
        .order('performed_date', { ascending: false });
      setMaintenanceRecords(data || []);
    } catch (error) {
      console.error('Error fetching maintenance records:', error);
    }
  };

  const handleAddRecord = async () => {
    if (!selectedTruck) return;

    try {
      const { error } = await supabase
        .from('maintenance_records')
        .insert([{
          truck_id: selectedTruck.id,
          maintenance_type: newRecord.maintenance_type,
          description: newRecord.description,
          performed_at_km: parseInt(newRecord.performed_at_km) || selectedTruck.current_odometer_km,
          performed_date: newRecord.performed_date,
          cost: parseFloat(newRecord.cost) || null,
          next_due_km: parseInt(newRecord.next_due_km) || null
        }]);

      if (error) throw error;

      if (parseInt(newRecord.performed_at_km) > selectedTruck.current_odometer_km) {
        await supabase
          .from('trucks')
          .update({ current_odometer_km: parseInt(newRecord.performed_at_km) })
          .eq('id', selectedTruck.id);
      }

      setShowAddRecord(false);
      setNewRecord({
        maintenance_type: 'oil_change',
        description: '',
        performed_at_km: '',
        performed_date: new Date().toISOString().split('T')[0],
        cost: '',
        next_due_km: ''
      });
      fetchMaintenanceRecords(selectedTruck.id);
      fetchData();
    } catch (error) {
      console.error('Error adding maintenance record:', error);
      console.error('Failed to add maintenance record:', error.message);
    }
  };

  const handleCreateWorkOrder = () => {
    const newOrder = {
      id: Date.now(),
      ...newWorkOrder,
      truck: trucks.find(t => t.id === newWorkOrder.truck_id),
      created_at: new Date().toISOString()
    };
    setWorkOrders([newOrder, ...workOrders]);
    setShowAddWorkOrder(false);
    setNewWorkOrder({
      truck_id: '',
      title: '',
      description: '',
      priority: 'medium',
      status: 'open',
      assigned_to: '',
      estimated_cost: '',
      due_date: ''
    });
  };

  const handleUpdateWorkOrderStatus = (orderId, newStatus) => {
    setWorkOrders(workOrders.map(order => 
      order.id === orderId ? { ...order, status: newStatus } : order
    ));
  };

  const handleConvertRequestToWorkOrder = (request) => {
    setNewWorkOrder({
      truck_id: request.truck_id,
      title: `Driver Request: ${request.issue_category}`,
      description: request.description,
      priority: request.urgency === 'high' ? 'high' : request.urgency === 'medium' ? 'medium' : 'low',
      status: 'open',
      assigned_to: '',
      estimated_cost: '',
      due_date: ''
    });
    setShowAddWorkOrder(true);
  };

  const getMaintenanceAlerts = () => {
    const alerts = [];
    trucks.forEach(truck => {
      maintenanceTypes.forEach(type => {
        if (type.interval === 0) return;
        const lastRecord = maintenanceRecords.find(r => 
          r.truck_id === truck.id && r.maintenance_type === type.value
        );
        const lastKm = lastRecord?.performed_at_km || 0;
        const nextDueKm = lastRecord?.next_due_km || (lastKm + type.interval);
        const kmUntilDue = nextDueKm - (truck.current_odometer_km || 0);
        
        if (kmUntilDue <= 0) {
          alerts.push({ truck, type: type.label, status: 'overdue', kmUntilDue, nextDueKm });
        } else if (kmUntilDue <= 500) {
          alerts.push({ truck, type: type.label, status: 'warning', kmUntilDue, nextDueKm });
        }
      });
    });
    return alerts.sort((a, b) => a.kmUntilDue - b.kmUntilDue);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'overdue': return 'var(--color-danger)';
      case 'warning': return 'var(--color-warning)';
      default: return 'var(--color-success)';
    }
  };

  const formatDate = (dateString) => {
    // __TZ_PICKER__ performed_date / due_date are calendar dates; new Date('YYYY-MM-DD')
    // is UTC midnight and displayed as the previous day in Ontario.
    const d = localDateFromYMD(dateString);
    if (!d) return '-';
    return d.toLocaleDateString('en-CA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(amount);
  };

  const alerts = getMaintenanceAlerts();
  const openWorkOrders = workOrders.filter(wo => wo.status !== 'completed');
  const pendingRequests = driverRequests.filter(r => r.status === 'pending');

  if (loading) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: '3rem', 
        color: 'var(--accent-blue)',
        fontSize: '1.5rem'
      }}>
        Loading maintenance data...
      </div>
    );
  }

  return (
    <div style={{ padding: '1rem 0' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1.5rem'
      }}>
        <div>
          <h2 style={{ color: 'var(--text-primary)', margin: '0 0 0.25rem 0' }}>
            Fleet Maintenance
          </h2>
          <p style={{ color: 'var(--text-secondary)', margin: 0, fontSize: '0.9rem' }}>
            Manage maintenance schedules, work orders, and driver requests
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button
            onClick={() => setShowAddWorkOrder(true)}
            style={{
              padding: '0.6rem 1rem',
              backgroundColor: 'var(--btn-primary-bg)',
              color: 'var(--text-primary)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              cursor: 'pointer',
              fontWeight: '500',
              fontSize: '0.9rem'
            }}
          >
            + New Work Order
          </button>
          <button
            onClick={() => setShowAddRecord(true)}
            style={{
              padding: '0.6rem 1rem',
              backgroundColor: 'var(--btn-secondary-bg)',
              color: 'var(--text-primary)',
              border: '1px solid var(--border-default)',
              borderRadius: 'var(--radius-md)',
              cursor: 'pointer',
              fontWeight: '500',
              fontSize: '0.9rem'
            }}
          >
            + Log Service
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div style={{
        display: 'flex',
        gap: '0.5rem',
        marginBottom: '1.5rem',
        padding: '0.5rem',
        backgroundColor: 'var(--surface-2)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--border-default)'
      }}>
        {[
          { id: 'dashboard', label: 'Dashboard' },
          { id: 'work_orders', label: 'Work Orders', count: openWorkOrders.length },
          { id: 'requests', label: 'Driver Requests', count: pendingRequests.length },
          { id: 'assets', label: 'Assets' },
          { id: 'history', label: 'Service History' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: activeTab === tab.id ? 'var(--btn-primary-bg)' : 'transparent',
              color: 'var(--text-primary)',
              border: activeTab === tab.id ? 'none' : '1px solid transparent',
              borderRadius: 'var(--radius-md)',
              cursor: 'pointer',
              fontWeight: activeTab === tab.id ? '600' : '400',
              fontSize: '0.85rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            {tab.label}
            {tab.count > 0 && (
              <span style={{
                backgroundColor: activeTab === tab.id ? 'rgba(255,255,255,0.2)' : 'var(--color-danger)',
                color: 'var(--text-primary)',
                padding: '0.1rem 0.5rem',
                borderRadius: '10px',
                fontSize: '0.75rem'
              }}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && (
        <div>
          {/* Summary Cards */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(4, 1fr)', 
            gap: '1rem',
            marginBottom: '1.5rem'
          }}>
            <div style={{
              backgroundColor: 'var(--surface-1)',
              borderRadius: 'var(--radius-lg)',
              padding: '1.25rem',
              border: '1px solid var(--border-default)'
            }}>
              <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                Overdue Items
              </div>
              <div style={{ 
                color: alerts.filter(a => a.status === 'overdue').length > 0 ? 'var(--color-danger)' : 'var(--color-success)', 
                fontSize: '2rem', 
                fontWeight: 'bold' 
              }}>
                {alerts.filter(a => a.status === 'overdue').length}
              </div>
            </div>
            <div style={{
              backgroundColor: 'var(--surface-1)',
              borderRadius: 'var(--radius-lg)',
              padding: '1.25rem',
              border: '1px solid var(--border-default)'
            }}>
              <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                Due Soon
              </div>
              <div style={{ 
                color: 'var(--color-warning)', 
                fontSize: '2rem', 
                fontWeight: 'bold' 
              }}>
                {alerts.filter(a => a.status === 'warning').length}
              </div>
            </div>
            <div style={{
              backgroundColor: 'var(--surface-1)',
              borderRadius: 'var(--radius-lg)',
              padding: '1.25rem',
              border: '1px solid var(--border-default)'
            }}>
              <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                Open Work Orders
              </div>
              <div style={{ 
                color: 'var(--accent-blue)', 
                fontSize: '2rem', 
                fontWeight: 'bold' 
              }}>
                {openWorkOrders.length}
              </div>
            </div>
            <div style={{
              backgroundColor: 'var(--surface-1)',
              borderRadius: 'var(--radius-lg)',
              padding: '1.25rem',
              border: '1px solid var(--border-default)'
            }}>
              <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                Driver Requests
              </div>
              <div style={{ 
                color: pendingRequests.length > 0 ? 'var(--color-warning)' : 'var(--text-secondary)', 
                fontSize: '2rem', 
                fontWeight: 'bold' 
              }}>
                {pendingRequests.length}
              </div>
            </div>
          </div>

          {/* Alerts Section */}
          {alerts.length > 0 && (
            <div style={{
              backgroundColor: 'var(--surface-1)',
              borderRadius: 'var(--radius-lg)',
              padding: '1.25rem',
              border: '1px solid var(--border-default)',
              marginBottom: '1.5rem'
            }}>
              <h3 style={{ color: 'var(--text-primary)', margin: '0 0 1rem 0', fontSize: '1rem' }}>
                Maintenance Alerts
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {alerts.slice(0, 5).map((alert, idx) => (
                  <div
                    key={idx}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '0.75rem',
                      backgroundColor: 'var(--surface-2)',
                      borderRadius: 'var(--radius-md)',
                      borderLeft: `4px solid ${getStatusColor(alert.status)}`
                    }}
                  >
                    <div>
                      <span style={{ color: 'var(--text-primary)', fontWeight: '500' }}>
                        Truck {alert.truck.truck_number}
                      </span>
                      <span style={{ color: 'var(--text-secondary)', margin: '0 0.5rem' }}>-</span>
                      <span style={{ color: 'var(--text-secondary)' }}>{alert.type}</span>
                    </div>
                    <span style={{ 
                      color: getStatusColor(alert.status),
                      fontWeight: alert.status === 'overdue' ? 'bold' : 'normal',
                      fontSize: '0.9rem'
                    }}>
                      {alert.status === 'overdue' 
                        ? `Overdue by ${Math.abs(alert.kmUntilDue).toLocaleString()} km`
                        : `Due in ${alert.kmUntilDue.toLocaleString()} km`
                      }
                    </span>
                  </div>
                ))}
              </div>
              {alerts.length > 5 && (
                <button
                  onClick={() => setActiveTab('assets')}
                  style={{
                    marginTop: '1rem',
                    padding: '0.5rem 1rem',
                    backgroundColor: 'transparent',
                    color: 'var(--accent-blue)',
                    border: '1px solid var(--accent-blue)',
                    borderRadius: 'var(--radius-md)',
                    cursor: 'pointer',
                    fontSize: '0.85rem'
                  }}
                >
                  View All {alerts.length} Alerts
                </button>
              )}
            </div>
          )}

          {/* Recent Activity */}
          <div style={{
            backgroundColor: 'var(--surface-1)',
            borderRadius: 'var(--radius-lg)',
            padding: '1.25rem',
            border: '1px solid var(--border-default)'
          }}>
            <h3 style={{ color: 'var(--text-primary)', margin: '0 0 1rem 0', fontSize: '1rem' }}>
              Recent Service History
            </h3>
            {maintenanceRecords.length === 0 ? (
              <p style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: '2rem' }}>
                No recent maintenance records
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {maintenanceRecords.slice(0, 5).map(record => (
                  <div
                    key={record.id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '0.75rem',
                      backgroundColor: 'var(--surface-2)',
                      borderRadius: 'var(--radius-md)'
                    }}
                  >
                    <div>
                      <span style={{ color: 'var(--text-primary)', fontWeight: '500', textTransform: 'capitalize' }}>
                        {record.maintenance_type.replace(/_/g, ' ')}
                      </span>
                      <span style={{ color: 'var(--text-secondary)', margin: '0 0.5rem' }}>-</span>
                      <span style={{ color: 'var(--text-secondary)' }}>
                        {record.performed_at_km?.toLocaleString()} km
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <span style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                        {formatDate(record.performed_date)}
                      </span>
                      {record.cost && (
                        <span style={{ color: 'var(--color-success)', fontSize: '0.85rem' }}>
                          {formatCurrency(record.cost)}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Work Orders Tab */}
      {activeTab === 'work_orders' && (
        <div>
          {workOrders.length === 0 ? (
            <div style={{
              backgroundColor: 'var(--surface-1)',
              borderRadius: 'var(--radius-lg)',
              padding: '3rem',
              border: '1px solid var(--border-default)',
              textAlign: 'center'
            }}>
              <p style={{ color: 'var(--text-secondary)', marginBottom: '1rem' }}>
                No work orders yet. Create one to track maintenance tasks.
              </p>
              <button
                onClick={() => setShowAddWorkOrder(true)}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: 'var(--btn-primary-bg)',
                  color: 'var(--text-primary)',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                + Create Work Order
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {workOrders.map(order => (
                <div
                  key={order.id}
                  style={{
                    backgroundColor: 'var(--surface-1)',
                    borderRadius: 'var(--radius-lg)',
                    padding: '1.25rem',
                    border: '1px solid var(--border-default)'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                    <div>
                      <h4 style={{ color: 'var(--text-primary)', margin: '0 0 0.25rem 0' }}>
                        {order.title}
                      </h4>
                      <p style={{ color: 'var(--text-secondary)', margin: 0, fontSize: '0.85rem' }}>
                        Truck {order.truck?.truck_number || 'N/A'}
                      </p>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <span style={{
                        padding: '0.25rem 0.75rem',
                        backgroundColor: PRIORITY_LEVELS.find(p => p.id === order.priority)?.color || '#8b949e',
                        color: '#fff',
                        borderRadius: '12px',
                        fontSize: '0.75rem',
                        fontWeight: '500'
                      }}>
                        {order.priority}
                      </span>
                      <select
                        value={order.status}
                        onChange={(e) => handleUpdateWorkOrderStatus(order.id, e.target.value)}
                        style={{
                          padding: '0.25rem 0.5rem',
                          backgroundColor: 'var(--surface-2)',
                          border: '1px solid var(--border-default)',
                          borderRadius: 'var(--radius-sm)',
                          color: 'var(--text-primary)',
                          fontSize: '0.75rem',
                          cursor: 'pointer'
                        }}
                      >
                        {WORK_ORDER_STATUSES.map(status => (
                          <option key={status.id} value={status.id}>{status.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  {order.description && (
                    <p style={{ color: 'var(--text-secondary)', margin: '0 0 1rem 0', fontSize: '0.9rem' }}>
                      {order.description}
                    </p>
                  )}
                  <div style={{ display: 'flex', gap: '1.5rem', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                    {order.due_date && (
                      <span>Due: {formatDate(order.due_date)}</span>
                    )}
                    {order.estimated_cost && (
                      <span>Est. Cost: {formatCurrency(order.estimated_cost)}</span>
                    )}
                    <span>Created: {formatDate(order.created_at)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Driver Requests Tab */}
      {activeTab === 'requests' && (
        <div>
          {driverRequests.length === 0 ? (
            <div style={{
              backgroundColor: 'var(--surface-1)',
              borderRadius: 'var(--radius-lg)',
              padding: '3rem',
              border: '1px solid var(--border-default)',
              textAlign: 'center'
            }}>
              <p style={{ color: 'var(--text-secondary)' }}>
                No driver maintenance requests. Requests submitted from the driver app will appear here.
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {driverRequests.map(request => (
                <div
                  key={request.id}
                  style={{
                    backgroundColor: 'var(--surface-1)',
                    borderRadius: 'var(--radius-lg)',
                    padding: '1.25rem',
                    border: '1px solid var(--border-default)'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                    <div>
                      <h4 style={{ color: 'var(--text-primary)', margin: '0 0 0.25rem 0', textTransform: 'capitalize' }}>
                        {request.issue_category?.replace(/_/g, ' ') || 'Maintenance Request'}
                      </h4>
                      <p style={{ color: 'var(--text-secondary)', margin: 0, fontSize: '0.85rem' }}>
                        Truck {request.truck?.truck_number || 'N/A'} - {request.driver?.user_id?.name || 'Unknown Driver'}
                      </p>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <span style={{
                        padding: '0.25rem 0.75rem',
                        backgroundColor: request.urgency === 'high' ? 'var(--color-danger)' : 
                                        request.urgency === 'medium' ? 'var(--color-warning)' : 'var(--text-secondary)',
                        color: '#fff',
                        borderRadius: '12px',
                        fontSize: '0.75rem',
                        fontWeight: '500'
                      }}>
                        {request.urgency || 'low'}
                      </span>
                      <span style={{
                        padding: '0.25rem 0.75rem',
                        backgroundColor: request.status === 'pending' ? 'var(--accent-blue)' : 'var(--color-success)',
                        color: '#fff',
                        borderRadius: '12px',
                        fontSize: '0.75rem',
                        fontWeight: '500'
                      }}>
                        {request.status || 'pending'}
                      </span>
                    </div>
                  </div>
                  {request.description && (
                    <p style={{ color: 'var(--text-secondary)', margin: '0 0 1rem 0', fontSize: '0.9rem' }}>
                      {request.description}
                    </p>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                      {formatDate(request.created_at)}
                    </span>
                    {request.status === 'pending' && (
                      <button
                        onClick={() => handleConvertRequestToWorkOrder(request)}
                        style={{
                          padding: '0.5rem 1rem',
                          backgroundColor: 'var(--btn-primary-bg)',
                          color: 'var(--text-primary)',
                          border: 'none',
                          borderRadius: 'var(--radius-md)',
                          cursor: 'pointer',
                          fontSize: '0.85rem'
                        }}
                      >
                        Create Work Order
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Assets Tab */}
      {activeTab === 'assets' && (
        <div>
          <div style={{ marginBottom: '1rem' }}>
            <select
              value={selectedTruck?.id || ''}
              onChange={(e) => setSelectedTruck(trucks.find(t => t.id === e.target.value))}
              style={{
                padding: '0.75rem',
                backgroundColor: 'var(--surface-input)',
                border: '1px solid var(--border-default)',
                borderRadius: 'var(--radius-md)',
                color: 'var(--text-primary)',
                fontSize: '1rem',
                minWidth: '300px'
              }}
            >
              {trucks.map(truck => (
                <option key={truck.id} value={truck.id}>
                  Truck {truck.truck_number} - {truck.license_plate} ({truck.current_odometer_km?.toLocaleString()} km)
                </option>
              ))}
            </select>
          </div>

          {selectedTruck && (
            <>
              {/* Truck Info Card */}
              <div style={{
                backgroundColor: 'var(--surface-1)',
                borderRadius: 'var(--radius-lg)',
                padding: '1.25rem',
                border: '1px solid var(--border-default)',
                marginBottom: '1.5rem'
              }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem' }}>
                  <div>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', marginBottom: '0.25rem' }}>TRUCK NUMBER</div>
                    <div style={{ color: 'var(--text-primary)', fontSize: '1.1rem', fontWeight: 'bold' }}>
                      {selectedTruck.truck_number}
                    </div>
                  </div>
                  <div>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', marginBottom: '0.25rem' }}>LICENSE PLATE</div>
                    <div style={{ color: 'var(--text-primary)', fontSize: '1.1rem' }}>
                      {selectedTruck.license_plate}
                    </div>
                  </div>
                  <div>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', marginBottom: '0.25rem' }}>ODOMETER</div>
                    <div style={{ color: 'var(--color-success)', fontSize: '1.1rem', fontWeight: 'bold' }}>
                      {selectedTruck.current_odometer_km?.toLocaleString()} km
                    </div>
                  </div>
                  <div>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', marginBottom: '0.25rem' }}>FUEL TYPE</div>
                    <div style={{ color: 'var(--text-primary)', fontSize: '1.1rem', textTransform: 'capitalize' }}>
                      {selectedTruck.fuel_type}
                    </div>
                  </div>
                </div>
              </div>

              {/* Maintenance Status Grid */}
              <div style={{
                backgroundColor: 'var(--surface-1)',
                borderRadius: 'var(--radius-lg)',
                padding: '1.25rem',
                border: '1px solid var(--border-default)'
              }}>
                <h3 style={{ color: 'var(--text-primary)', margin: '0 0 1rem 0', fontSize: '1rem' }}>
                  Maintenance Status
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem' }}>
                  {maintenanceTypes.filter(t => t.interval > 0).map(type => {
                    const lastRecord = maintenanceRecords.find(r => r.maintenance_type === type.value);
                    const lastKm = lastRecord?.performed_at_km || 0;
                    const nextDueKm = lastRecord?.next_due_km || (lastKm + type.interval);
                    const kmUntilDue = nextDueKm - selectedTruck.current_odometer_km;
                    const status = kmUntilDue <= 0 ? 'overdue' : kmUntilDue <= 500 ? 'warning' : 'ok';
                    
                    return (
                      <div
                        key={type.value}
                        style={{
                          backgroundColor: 'var(--surface-2)',
                          borderRadius: 'var(--radius-md)',
                          padding: '0.75rem',
                          borderLeft: `4px solid ${getStatusColor(status)}`
                        }}
                      >
                        <div style={{ color: 'var(--text-primary)', fontWeight: '500', marginBottom: '0.25rem' }}>
                          {type.label}
                        </div>
                        <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                          Last: {lastRecord ? `${lastKm.toLocaleString()} km` : 'Never'}
                        </div>
                        <div style={{ 
                          color: getStatusColor(status), 
                          fontSize: '0.85rem',
                          fontWeight: status !== 'ok' ? 'bold' : 'normal'
                        }}>
                          {status === 'overdue' 
                            ? `Overdue by ${Math.abs(kmUntilDue).toLocaleString()} km`
                            : status === 'warning'
                            ? `Due in ${kmUntilDue.toLocaleString()} km`
                            : `Due at ${nextDueKm.toLocaleString()} km`
                          }
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Service History Tab */}
      {activeTab === 'history' && (
        <div>
          <div style={{ marginBottom: '1rem' }}>
            <select
              value={selectedTruck?.id || ''}
              onChange={(e) => setSelectedTruck(trucks.find(t => t.id === e.target.value))}
              style={{
                padding: '0.75rem',
                backgroundColor: 'var(--surface-input)',
                border: '1px solid var(--border-default)',
                borderRadius: 'var(--radius-md)',
                color: 'var(--text-primary)',
                fontSize: '1rem',
                minWidth: '300px'
              }}
            >
              {trucks.map(truck => (
                <option key={truck.id} value={truck.id}>
                  Truck {truck.truck_number}
                </option>
              ))}
            </select>
          </div>

          <div style={{
            backgroundColor: 'var(--surface-1)',
            borderRadius: 'var(--radius-lg)',
            padding: '1.25rem',
            border: '1px solid var(--border-default)'
          }}>
            {maintenanceRecords.length === 0 ? (
              <p style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: '2rem' }}>
                No maintenance records found for this truck
              </p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border-default)' }}>
                    <th style={{ padding: '0.75rem', textAlign: 'left', color: 'var(--text-secondary)', fontWeight: '500' }}>Date</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', color: 'var(--text-secondary)', fontWeight: '500' }}>Type</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', color: 'var(--text-secondary)', fontWeight: '500' }}>Odometer</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', color: 'var(--text-secondary)', fontWeight: '500' }}>Cost</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', color: 'var(--text-secondary)', fontWeight: '500' }}>Next Due</th>
                  </tr>
                </thead>
                <tbody>
                  {maintenanceRecords.map(record => (
                    <tr key={record.id} style={{ borderBottom: '1px solid var(--border-muted)' }}>
                      <td style={{ padding: '0.75rem', color: 'var(--text-primary)' }}>
                        {formatDate(record.performed_date)}
                      </td>
                      <td style={{ padding: '0.75rem', color: 'var(--text-primary)', textTransform: 'capitalize' }}>
                        {record.maintenance_type.replace(/_/g, ' ')}
                      </td>
                      <td style={{ padding: '0.75rem', color: 'var(--text-primary)' }}>
                        {record.performed_at_km?.toLocaleString()} km
                      </td>
                      <td style={{ padding: '0.75rem', color: 'var(--text-primary)' }}>
                        {formatCurrency(record.cost)}
                      </td>
                      <td style={{ padding: '0.75rem', color: 'var(--text-secondary)' }}>
                        {record.next_due_km ? `${record.next_due_km.toLocaleString()} km` : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Add Work Order Modal */}
      {showAddWorkOrder && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--surface-1)',
            borderRadius: 'var(--radius-xl)',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%',
            border: '1px solid var(--border-default)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ color: 'var(--text-primary)', margin: 0 }}>Create Work Order</h3>
              <button
                onClick={() => setShowAddWorkOrder(false)}
                style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', fontSize: '1.5rem', cursor: 'pointer' }}
              >
                x
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', color: 'var(--text-secondary)', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                  Truck *
                </label>
                <select
                  value={newWorkOrder.truck_id}
                  onChange={(e) => setNewWorkOrder({ ...newWorkOrder, truck_id: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    backgroundColor: 'var(--surface-input)',
                    border: '1px solid var(--border-default)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--text-primary)'
                  }}
                >
                  <option value="">Select Truck</option>
                  {trucks.map(truck => (
                    <option key={truck.id} value={truck.id}>
                      Truck {truck.truck_number} - {truck.license_plate}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: 'block', color: 'var(--text-secondary)', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                  Title *
                </label>
                <input
                  type="text"
                  value={newWorkOrder.title}
                  onChange={(e) => setNewWorkOrder({ ...newWorkOrder, title: e.target.value })}
                  placeholder="e.g., Replace brake pads"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    backgroundColor: 'var(--surface-input)',
                    border: '1px solid var(--border-default)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--text-primary)',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: 'var(--text-secondary)', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                  Description
                </label>
                <textarea
                  value={newWorkOrder.description}
                  onChange={(e) => setNewWorkOrder({ ...newWorkOrder, description: e.target.value })}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    backgroundColor: 'var(--surface-input)',
                    border: '1px solid var(--border-default)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--text-primary)',
                    resize: 'vertical',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', color: 'var(--text-secondary)', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                    Priority
                  </label>
                  <select
                    value={newWorkOrder.priority}
                    onChange={(e) => setNewWorkOrder({ ...newWorkOrder, priority: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      backgroundColor: 'var(--surface-input)',
                      border: '1px solid var(--border-default)',
                      borderRadius: 'var(--radius-md)',
                      color: 'var(--text-primary)'
                    }}
                  >
                    {PRIORITY_LEVELS.map(p => (
                      <option key={p.id} value={p.id}>{p.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', color: 'var(--text-secondary)', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                    Due Date
                  </label>
                  <input
                    type="date"
                    value={newWorkOrder.due_date}
                    onChange={(e) => setNewWorkOrder({ ...newWorkOrder, due_date: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      backgroundColor: 'var(--surface-input)',
                      border: '1px solid var(--border-default)',
                      borderRadius: 'var(--radius-md)',
                      color: 'var(--text-primary)',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', color: 'var(--text-secondary)', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                  Estimated Cost
                </label>
                <input
                  type="number"
                  value={newWorkOrder.estimated_cost}
                  onChange={(e) => setNewWorkOrder({ ...newWorkOrder, estimated_cost: e.target.value })}
                  placeholder="0.00"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    backgroundColor: 'var(--surface-input)',
                    border: '1px solid var(--border-default)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--text-primary)',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                <button
                  onClick={() => setShowAddWorkOrder(false)}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    backgroundColor: 'var(--btn-secondary-bg)',
                    color: 'var(--text-primary)',
                    border: '1px solid var(--border-default)',
                    borderRadius: 'var(--radius-md)',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateWorkOrder}
                  disabled={!newWorkOrder.truck_id || !newWorkOrder.title}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    backgroundColor: newWorkOrder.truck_id && newWorkOrder.title ? 'var(--btn-primary-bg)' : 'var(--surface-3)',
                    color: 'var(--text-primary)',
                    border: 'none',
                    borderRadius: 'var(--radius-md)',
                    cursor: newWorkOrder.truck_id && newWorkOrder.title ? 'pointer' : 'not-allowed',
                    fontWeight: '500'
                  }}
                >
                  Create Work Order
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Service Record Modal */}
      {showAddRecord && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--surface-1)',
            borderRadius: 'var(--radius-xl)',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%',
            border: '1px solid var(--border-default)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ color: 'var(--text-primary)', margin: 0 }}>Log Service Record</h3>
              <button
                onClick={() => setShowAddRecord(false)}
                style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', fontSize: '1.5rem', cursor: 'pointer' }}
              >
                x
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', color: 'var(--text-secondary)', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                  Truck
                </label>
                <select
                  value={selectedTruck?.id || ''}
                  onChange={(e) => setSelectedTruck(trucks.find(t => t.id === e.target.value))}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    backgroundColor: 'var(--surface-input)',
                    border: '1px solid var(--border-default)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--text-primary)'
                  }}
                >
                  {trucks.map(truck => (
                    <option key={truck.id} value={truck.id}>
                      Truck {truck.truck_number} - {truck.license_plate}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: 'block', color: 'var(--text-secondary)', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                  Maintenance Type
                </label>
                <select
                  value={newRecord.maintenance_type}
                  onChange={(e) => {
                    const type = maintenanceTypes.find(t => t.value === e.target.value);
                    const nextDue = type?.interval ? (parseInt(newRecord.performed_at_km) || selectedTruck?.current_odometer_km || 0) + type.interval : '';
                    setNewRecord({ ...newRecord, maintenance_type: e.target.value, next_due_km: nextDue.toString() });
                  }}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    backgroundColor: 'var(--surface-input)',
                    border: '1px solid var(--border-default)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--text-primary)'
                  }}
                >
                  {maintenanceTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', color: 'var(--text-secondary)', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                    Odometer (km)
                  </label>
                  <input
                    type="number"
                    value={newRecord.performed_at_km}
                    onChange={(e) => setNewRecord({ ...newRecord, performed_at_km: e.target.value })}
                    placeholder={selectedTruck?.current_odometer_km?.toString() || '0'}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      backgroundColor: 'var(--surface-input)',
                      border: '1px solid var(--border-default)',
                      borderRadius: 'var(--radius-md)',
                      color: 'var(--text-primary)',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', color: 'var(--text-secondary)', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                    Date
                  </label>
                  <input
                    type="date"
                    value={newRecord.performed_date}
                    onChange={(e) => setNewRecord({ ...newRecord, performed_date: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      backgroundColor: 'var(--surface-input)',
                      border: '1px solid var(--border-default)',
                      borderRadius: 'var(--radius-md)',
                      color: 'var(--text-primary)',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', color: 'var(--text-secondary)', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                    Cost ($)
                  </label>
                  <input
                    type="number"
                    value={newRecord.cost}
                    onChange={(e) => setNewRecord({ ...newRecord, cost: e.target.value })}
                    placeholder="0.00"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      backgroundColor: 'var(--surface-input)',
                      border: '1px solid var(--border-default)',
                      borderRadius: 'var(--radius-md)',
                      color: 'var(--text-primary)',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', color: 'var(--text-secondary)', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                    Next Due (km)
                  </label>
                  <input
                    type="number"
                    value={newRecord.next_due_km}
                    onChange={(e) => setNewRecord({ ...newRecord, next_due_km: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      backgroundColor: 'var(--surface-input)',
                      border: '1px solid var(--border-default)',
                      borderRadius: 'var(--radius-md)',
                      color: 'var(--text-primary)',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', color: 'var(--text-secondary)', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                  Notes
                </label>
                <textarea
                  value={newRecord.description}
                  onChange={(e) => setNewRecord({ ...newRecord, description: e.target.value })}
                  rows={2}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    backgroundColor: 'var(--surface-input)',
                    border: '1px solid var(--border-default)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--text-primary)',
                    resize: 'vertical',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                <button
                  onClick={() => setShowAddRecord(false)}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    backgroundColor: 'var(--btn-secondary-bg)',
                    color: 'var(--text-primary)',
                    border: '1px solid var(--border-default)',
                    borderRadius: 'var(--radius-md)',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddRecord}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    backgroundColor: 'var(--btn-primary-bg)',
                    color: 'var(--text-primary)',
                    border: 'none',
                    borderRadius: 'var(--radius-md)',
                    cursor: 'pointer',
                    fontWeight: '500'
                  }}
                >
                  Save Record
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MaintenanceView;
